import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import Layout from "@/pages/Layout";
import Dashboard from "@/pages/Dashboard";import Prezzi from "@/pages/Prezzi";
import ForwardPage from "@/pages/ForwardPage";
import News from "@/pages/News";
import NewsDetail from "@/pages/NewsDetail";
import Reports from "@/pages/Reports";
import Notifiche from "@/pages/Notifiche";
import Profilo from "@/pages/Profilo";
import AdminUpload from "@/pages/admin/AdminUpload";
import AdminUploads from "@/pages/admin/AdminUploads";
import AdminNews from "@/pages/admin/AdminNews";
import AdminReports from "@/pages/admin/AdminReports";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminLogs from "@/pages/admin/AdminLogs";
import AdminBriefing from "@/pages/admin/AdminBriefing";
import AdminAccessStats from "@/pages/admin/AdminAccessStats";
import AdminCustomReports from "@/pages/admin/AdminCustomReports";
import AdminEnergyAnalytics from "@/pages/admin/AdminEnergyAnalytics";
import AdminClientGroups from "@/pages/admin/AdminClientGroups";
import AdminEmailLog from "@/pages/admin/AdminEmailLog";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminAiSettings from "@/pages/admin/AdminAiSettings";
import AdminMarketImport from "@/pages/admin/AdminMarketImport";
import Briefings from "@/pages/Briefings";
import CustomReports from "@/pages/CustomReports";
import SpotGas from "@/pages/SpotGas";
import PriceAlerts from "@/pages/PriceAlerts";
import AdminPriceAlerts from "@/pages/admin/AdminPriceAlerts";
import Suspended from "@/pages/Suspended";
import EaLayout, { EaRedirect } from "@/pages/energy-analytics/EaLayout";
import EaOverview from "@/pages/energy-analytics/EaOverview";
import EaBudget from "@/pages/energy-analytics/EaBudget";
import EaPowerOverview from "@/pages/energy-analytics/EaPowerOverview";
import EaGasDaily from "@/pages/energy-analytics/EaGasDaily";
import EaGasOverview from "@/pages/energy-analytics/EaGasOverview";
import EaEfficiency from "@/pages/energy-analytics/EaEfficiency";
import EaSustainability from "@/pages/energy-analytics/EaSustainability";
import EaGasConsumption from "@/pages/energy-analytics/EaGasConsumption";
import EaGasCosts from "@/pages/energy-analytics/EaGasCosts";
import EaGasComparePoints from "@/pages/energy-analytics/EaGasComparePoints";
import EaGasComparePeriods from "@/pages/energy-analytics/EaGasComparePeriods";
import EaPowerConsumption from "@/pages/energy-analytics/EaPowerConsumption";
import EaPowerCosts from "@/pages/energy-analytics/EaPowerCosts";
import EaPowerProfiles from "@/pages/energy-analytics/EaPowerProfiles";
import EaPowerHeatmap from "@/pages/energy-analytics/EaPowerHeatmap";
import EaPowerPeaks from "@/pages/energy-analytics/EaPowerPeaks";
import EaPowerPower from "@/pages/energy-analytics/EaPowerPower";
import EaPowerComparePoints from "@/pages/energy-analytics/EaPowerComparePoints";
import EaPowerComparePeriods from "@/pages/energy-analytics/EaPowerComparePeriods";
import EaPowerPun from "@/pages/energy-analytics/EaPowerPun";
import EaPowerLoadShift from "@/pages/energy-analytics/EaPowerLoadShift";
import "@/App.css";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-400">Caricamento…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  // Blocco accesso ai clienti sospesi (manuale OR abbonamento scaduto).
  // Lo staff bypassa, e la pagina /sospeso è esclusa (altrimenti loop).
  if (user.role === "cliente" && user.is_suspended) return <Navigate to="/sospeso" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sospeso" element={<Suspended />} />
          <Route path="/energy-analytics" element={<Protected><EaLayout /></Protected>}>
            <Route index element={<EaRedirect />} />
            <Route path="overview" element={<EaOverview />} />
            <Route path="overview/budget" element={<EaBudget />} />
            <Route path="power/panoramica" element={<EaPowerOverview />} />
            <Route path="power/consumi" element={<EaPowerConsumption />} />
            <Route path="power/costi" element={<EaPowerCosts />} />
            <Route path="power/profili" element={<EaPowerProfiles />} />
            <Route path="power/heatmap" element={<EaPowerHeatmap />} />
            <Route path="power/picchi" element={<EaPowerPeaks />} />
            <Route path="power/potenza" element={<EaPowerPower />} />
            <Route path="power/confronto-pod" element={<EaPowerComparePoints />} />
            <Route path="power/confronto-periodi" element={<EaPowerComparePeriods />} />
            <Route path="power/pun" element={<EaPowerPun />} />
            <Route path="power/simulazione-carichi" element={<EaPowerLoadShift />} />
            <Route path="gas/panoramica" element={<EaGasOverview />} />
            <Route path="kpi-sostenibilita/kpi" element={<EaEfficiency />} />
            <Route path="kpi-sostenibilita/sostenibilita" element={<EaSustainability />} />
            <Route path="power/kpi-efficienza" element={<Navigate to="/energy-analytics/kpi-sostenibilita/kpi" replace />} />
            <Route path="gas/kpi-efficienza" element={<Navigate to="/energy-analytics/kpi-sostenibilita/kpi" replace />} />
            <Route path="gas/consumi" element={<EaGasConsumption />} />
            <Route path="gas/costi" element={<EaGasCosts />} />
            <Route path="gas/profili-giornalieri" element={<EaGasDaily />} />
            <Route path="gas/confronto-punti" element={<EaGasComparePoints />} />
            <Route path="gas/confronto-periodi" element={<EaGasComparePeriods />} />
            <Route path="*" element={<EaRedirect />} />
          </Route>
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="prezzi" element={<Prezzi />} />
            <Route path="forward/ttf" element={<ForwardPage sheet="TTF" title="Forward Gas TTF" />} />
            <Route path="forward/psv" element={<ForwardPage sheet="PSV" title="Forward Gas PSV" />} />
            <Route path="forward/power" element={<ForwardPage sheet="Power" title="Forward Power" />} />
            <Route path="forward/co2" element={<ForwardPage sheet="CO2" title="Forward CO2" />} />
            <Route path="news" element={<News />} />
            <Route path="news/:id" element={<NewsDetail />} />
            <Route path="report" element={<Reports />} />
            <Route path="report-personalizzati" element={<CustomReports />} />
            <Route path="psv-ttf-da-mid" element={<SpotGas />} />
            <Route path="alert-prezzi" element={<PriceAlerts />} />
            <Route path="notifiche" element={<Notifiche />} />
            <Route path="briefing" element={<Briefings />} />
            <Route path="briefing/:id" element={<Briefings />} />
            <Route path="profilo" element={<Profilo />} />

            <Route path="admin/upload" element={<Protected roles={["admin", "editor"]}><AdminUpload /></Protected>} />
            <Route path="admin/uploads" element={<Protected roles={["admin", "editor"]}><AdminUploads /></Protected>} />
            <Route path="admin/aggiornamento-mercato" element={<Protected roles={["admin", "editor"]}><AdminMarketImport /></Protected>} />
            <Route path="admin/news" element={<Protected roles={["admin", "editor"]}><AdminNews /></Protected>} />
            <Route path="admin/report" element={<Protected roles={["admin", "editor"]}><AdminReports /></Protected>} />
            <Route path="admin/notifiche" element={<Protected roles={["admin", "editor"]}><AdminNotifications /></Protected>} />
            <Route path="admin/briefing" element={<Protected roles={["admin", "editor"]}><AdminBriefing /></Protected>} />
            <Route path="admin/clienti" element={<Protected roles={["admin", "editor"]}><AdminUsers /></Protected>} />
            <Route path="admin/gruppi-clienti" element={<Protected roles={["admin", "editor"]}><AdminClientGroups /></Protected>} />
            <Route path="admin/storico-email" element={<Protected roles={["admin", "editor"]}><AdminEmailLog /></Protected>} />
            <Route path="admin/staff" element={<Protected roles={["admin"]}><AdminStaff /></Protected>} />
            <Route path="admin/report-personalizzati" element={<Protected roles={["admin", "editor"]}><AdminCustomReports /></Protected>} />
            <Route path="admin/energy-analytics" element={<Protected roles={["admin", "editor"]}><AdminEnergyAnalytics /></Protected>} />
            <Route path="admin/accessi" element={<Protected roles={["admin", "editor"]}><AdminAccessStats /></Protected>} />
            <Route path="admin/alert-prezzi" element={<Protected roles={["admin", "editor"]}><AdminPriceAlerts /></Protected>} />
            <Route path="admin/ai" element={<Protected roles={["admin"]}><AdminAiSettings /></Protected>} />
            <Route path="admin/log" element={<Protected roles={["admin", "editor"]}><AdminLogs /></Protected>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
